import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "../../app/store/baseQuery";
import { qwCinemas } from "../../app/clients/qwCinemas";
import { formatNestedAddress } from "../../utils/address";

export const cinemasBaseApi = createApi({
  reducerPath: "cinemas",
  baseQuery: axiosBaseQuery(qwCinemas),
  endpoints: (builder) => ({
    getTheatres: builder.query({
      keepUnusedDataFor: 86400,
      query: ({ filter, q, ps, offset }) => ({
        url: "/v3/acs_facilities",
        method: "GET",
        params: {
          filter,
          q,
          ps,
          offset,
        },
      }),
      providesTags: ["Theatres"],
    }),

    addTheatre: builder.mutation({
      query: ({ id, acs_status }) => ({
        url: `/v3/acs_facilities`,
        method: "POST",
        data: {
          facility_id: id,
          ...(acs_status && { acs_status: acs_status }),
        },
      }),
      invalidatesTags: ["Theatres"],
    }),

    getSystemConstants: builder.query({
      query: () => ({
        url: "/v3/system_constants",
        method: "GET",
      }),
      providesTags: ["SystemConstants"],
      keepUnusedDataFor: 0,
    }),

    setSystemConstants: builder.mutation({
      query: ({
        radius,
        supported_android_version,
        supported_ios_version,
        server_username,
        server_password,
      }) => ({
        url: "/v3/system_constants",
        method: "POST",
        data: {
          radius,
          supported_android_version,
          supported_ios_version,
          server_username,
          server_password,
        },
      }),
      invalidatesTags: ["SystemConstants"],
    }),

    getTheatreById: builder.query({
      query: ({ id }) => ({
        url: `/v3/acs_facilities/${id}`,
        method: "GET",
      }),
      providesTags: ["Theatres"],
    }),

    checkTheatreByFacilityId: builder.query({
      queryFn: async ({ id }, _api, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: `/v3/acs_facilities/${id}`,
          method: "GET",
        });

        // Not found means the theatre is not yet added, which is a valid state for this check.
        if (result?.error?.status === 404) {
          return { data: null };
        }

        return result;
      },
      providesTags: ["Theatres"],
    }),

    addAcsDevice: builder.mutation({
      query: ({
        screen_id,
        screen_network_id,
        screen_network_password,
        acs_device_id,
        serial_number,
        device_ip,
        device_status,
        installed_at,
        installed_by,
        notes,
        confirm_remap,
      }) => ({
        url: `/v3/acs_devices`,
        method: "POST",
        data: {
          screen_id,
          screen_network_id,
          screen_network_password,
          acs_device_id,
          serial_number,
          device_ip,
          device_status,
          installed_at,
          installed_by,
          notes,
          confirm_remap,
        },
      }),
      invalidatesTags: (result, error) => (error ? [] : ["Theatres"]),
    }),

    getLocations: builder.query({
      query: ({ q }) => ({
        url: "/v3/locations/search",
        method: "GET",
        params: { q },
      }),
      transformResponse: (response) => {
        return response.map((location) => ({
          ...location,
          displayName: formatNestedAddress(location),
        }));
      },
    }),

    deleteAcsDevice: builder.mutation({
      query: ({ id }) => ({
        url: `/v3/acs_devices/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Theatres"],
    }),

    checkSerialNumberExists: builder.query({
      query: ({ serial_number, acs_device_id }) => ({
        url: `/v3/acs_devices/validate_serial_number`,
        method: "GET",
        params: {
          serial_number,
          acs_device_id,
        },
      }),
    }),
  }),
});

export const {
  useGetTheatresQuery,
  useAddTheatreMutation,
  useGetSystemConstantsQuery,
  useSetSystemConstantsMutation,
  useGetTheatreByIdQuery,
  useCheckTheatreByFacilityIdQuery,
  useAddAcsDeviceMutation,
  useGetLocationsQuery,
  useDeleteAcsDeviceMutation,
  useCheckSerialNumberExistsQuery,
} = cinemasBaseApi;
